/**
 * @file importService.js
 * @description APIs for import requests.
 **/

import { importHttp } from './httpService';
import qs from 'qs';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => window.localStorage.getItem('tenantId');

/**
 * Post import data for the current tenant.
 * @param {number} category - The import type category ID.
 * @param {number} errorThresholdPercentage - The allowed error threshold percentage.
 * @param {boolean} test - Flag indicating if it's a test import.
 * @param {number} processFormat - The file process format ID.
 * @param {File} file - The file to be imported.
 * @returns {Promise} The promise with the import data or throws an error.
 */
export const postImportData = async (
    category,
    errorThresholdPercentage,
    test,
    processFormat,
    file,
    importTemplateId,
) => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    const headers = {
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
    };

    const formData = new FormData();
    formData.append('TenantId', tenantId);
    formData.append('ImportTypeId', category);
    formData.append('ErrorThresholdPercentage', errorThresholdPercentage);
    formData.append('IsTest', test);
    formData.append('ImportFileTypeId', processFormat);
    formData.append('ImportTemplateId', importTemplateId);
    formData.append('File', file);

    try {
        const response = await importHttp.post(
            `/v2/import/tenant/${tenantId}/import`,
            formData,
            {
                headers,
            },
        );
        return response;
    } catch (error) {
        console.error('Error sending import data:', error);
        throw error;
    }
};

/**
 * Get import data for the current tenant.
 * @param {number} page - The page number for pagination.
 * @param {number} pageSize - The number of items per page.
 * @returns {Promise} The promise with the import data or null if error occurs.
 */
export const getImportData = async (
    page,
    pageSize,
    externalTenantId = null,
) => {
    const tenantId = getTenantId() || externalTenantId;
    if (!tenantId) {
        console.error('Tenant ID missing.');
        return null;
    }

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/import?page=${page}&pageSize=${pageSize}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import data:', error);
        return null;
    }
};

/**
 * Gets the last import run dates by import type for the tenant
 */
export const getImportLastRunDates = async externalTenantId => {
    const tenantId = getTenantId() || externalTenantId;

    if (!tenantId) {
        console.error('Tenant ID missing.');
        return null;
    }

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/import/last-run-dates`,
        );

        return response.data;
    } catch (error) {
        console.error('Error retrieving import data:', error);
        return null;
    }
};

export const getOriginalFile = async (id, tenantId) => {
    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/import/${id}/file`,
            {
                responseType: 'blob',
            },
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving original file:', error);
        return null;
    }
};

export const getImportReport = async (id, tenantId) => {
    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/import-report/${id}`,
            { responseType: 'blob' },
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving report:', error);
        return null;
    }
};

export const getImportTypeData = async () => {
    const tenantId = getTenantId();
    try {
        const response = await importHttp.get(
            `v2/import/import-lookup/import-type?tenantId=${tenantId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import type data:', error);
        return null;
    }
};

export const getImportFileTypeData = async () => {
    try {
        const response = await importHttp.get(
            `v2/import/import-lookup/import-file-type`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import file type data:', error);
        return null;
    }
};

// ================ Custom Imports ================ //
/**
 * Retrieves all import templates for a tenant.
 *
 * @returns {Promise<ImportTemplate[]>} A promise that resolves to an array of import templates.
 */
export const getAllImportTemplatesForTenant = async tenantId => {
    if (!tenantId) tenantId = getTenantId();

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/import-template`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import templates for tenant:', error);
        throw error;
    }
};

/**
 * Retrieves a specific import template.
 *
 * Backend now returns a template-level payload with nested `templateTypes[]`.
 * Each template type owns its own fields and mappings.
 *
 * @param {number} id - The ID of the import template.
 * @returns {Promise<CustomImportTemplatePayload>} A promise that resolves to the import template object.
 */
export const getImportTemplate = async id => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/import-template/${id}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import template:', error);
        throw error;
    }
};

/**
 * Creates a new import template.
 *
 * Expected payload shape:
 * - template-level file settings live on the root object
 * - import-type-specific fields and mappings live under `templateTypes[]`
 * - root `importTypeId` should be set to the first template type's `importTypeId`
 *   for backend compatibility
 *
 * @param {CustomImportTemplatePayload} templateData - The data for the new import template.
 * @returns {Promise<CustomImportTemplatePayload>} A promise that resolves to the created import template object.
 */
export const createImportTemplate = async templateData => {
    const tenantId = getTenantId();
    if (!templateData.tenantId) templateData.tenantId = tenantId;
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        const response = await importHttp.post(
            `/v2/import/tenant/${tenantId}/import-template`,
            templateData,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating import template:', error);
        throw error;
    }
};

/**
 * Updates an existing import template.
 *
 * Expected payload shape:
 * - template-level file settings live on the root object
 * - import-type-specific fields and mappings live under `templateTypes[]`
 * - root `importTypeId` should be set to the first template type's `importTypeId`
 *   for backend compatibility
 *
 * @param {CustomImportTemplatePayload} templateData - The updated data for the import template.
 * @returns {Promise<CustomImportTemplatePayload>} A promise that resolves to the updated import template object.
 */
export const updateImportTemplate = async templateData => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        const response = await importHttp.put(
            `/v2/import/tenant/${tenantId}/import-template`,
            templateData,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating import template:', error);
        throw error;
    }
};

/**
 * Deletes an import template.
 *
 * @param {number} id - The ID of the import template to delete.
 * @returns {Promise<void>} A promise that resolves when the import template is deleted.
 */
export const deleteImportTemplate = async id => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        await importHttp.delete(
            `/v2/import/tenant/${tenantId}/import-template/${id}`,
        );
    } catch (error) {
        console.error('Error deleting import template:', error);
        throw error;
    }
};

/**
 * Retrieves import file formats.
 *
 * @returns {Promise<ImportFileFormat[]>} A promise that resolves to an array of import file formats.
 */
export const getImportFileFormats = async () => {
    try {
        const response = await importHttp.get(
            '/v2/import/import-lookup/import-file-format',
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import file formats:', error);
        throw error;
    }
};

/**
 * Retrieves import file types.
 *
 * @returns {Promise<ImportFileType[]>} A promise that resolves to an array of import file types.
 */
export const getImportFileTypes = async () => {
    try {
        const response = await importHttp.get(
            '/v2/import/import-lookup/import-file-type',
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import file types:', error);
        throw error;
    }
};

/**
 * Retrieves import types.
 *
 * @returns {Promise<ImportType[]>} A promise that resolves to an array of import types.
 */
export const getImportTypes = async () => {
    const tenantId = getTenantId();
    try {
        const response = await importHttp.get(
            `/v2/import/import-lookup/import-type?tenantId=${tenantId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import types:', error);
        throw error;
    }
};

/**
 * Retrieves import delimiters.
 *
 * @returns {Promise<ImportDelimiter[]>} A promise that resolves to an array of import delimiters.
 */
export const getImportDelimiters = async () => {
    try {
        const response = await importHttp.get(
            '/v2/import/import-lookup/import-delimiter',
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import delimiters:', error);
        throw error;
    }
};

/**
 * Retrieves import fields for a specific import type.
 *
 * @param {number} importTypeId - The ID of the import type.
 * @returns {Promise<ImportField[]>} A promise that resolves to an array of import fields.
 */
export const getImportFields = async importTypeId => {
    try {
        const tenantId = getTenantId();
        const response = await importHttp.get(
            `/v2/import/import-lookup/import-field?importTypeId=${importTypeId}${tenantId === 0 ? '' : `&tenantId=${tenantId}`}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import fields:', error);
        throw error;
    }
};

/**
 * Retrieves operators.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of operators.
 */
export const getOperators = async () => {
    try {
        const response = await importHttp.get(
            '/v2/import/import-lookup/operator',
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving operators:', error);
        throw error;
    }
};

/**
 * Builds an import template using the provided file and criteria.
 *
 * @param {number} importTypeId - The ID of the import type.
 * @param {number} importFileFormatId - The ID of the import file format.
 * @param {number} [importDelimiterId] - The ID of the import delimiter (only for delimited files).
 * @param {string} [customDelimiter] - The custom delimiter (only for delimited files).
 * @param {string} [sheetName] - The sheet name (optional, only for Excel files).
 * @param {File} file - The file to be uploaded.
 * @returns {Promise<ImportTemplate>} A promise that resolves to the built import template object.
 */
export const buildImportTemplate = async (
    importTypeId,
    importFileFormatId,
    importDelimiterId,
    customDelimiter,
    sheetName,
    file,
) => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    const formData = new FormData();
    formData.append('File', file);
    formData.append('ImportTypeId', importTypeId);
    formData.append('ImportFileFormatId', importFileFormatId);

    if (importFileFormatId === 1) {
        if (importDelimiterId) {
            formData.append('ImportDelimiterId', importDelimiterId);
        }
        if (customDelimiter) {
            formData.append('CustomDelimiter', customDelimiter);
        }
    } else if (importFileFormatId === 2) {
        if (sheetName) {
            formData.append('SheetName', sheetName);
        }
    }

    try {
        const response = await importHttp.post(
            `/v2/import/tenant/${tenantId}/import-template-builder`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
            },
        );
        return response.data;
    } catch (error) {
        console.error('Error building import template:', error);
        throw error;
    }
};

/**
 * Get import data for the current tenant.
 * @returns {Promise} The promise with the import data or null if error occurs.
 */
export const getAutoImports = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
        console.error('Tenant ID missing.');
        return null;
    }

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/auto-import`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import data:', error);
        return null;
    }
};

/**
 * Gets a single auto import
 * @returns {Promise} The promise with the auto import or null
 */
export const getAutoImport = async id => {
    const tenantId = getTenantId();
    if (!tenantId) {
        console.error('Tenant ID missing.');
        return null;
    }

    if (!id) {
        console.error('ID missing.');
        return null;
    }

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/auto-import/${id}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import data:', error);
        return null;
    }
};

/**
 * Creates a new auto import
 *
 * @param {AutoImport} autoImport - The data for the new auto import
 * @returns {Promise<object>} A promise that resolves to the created auto import
 */
export const createAutoImport = async autoImport => {
    const tenantId = getTenantId();
    if (!autoImport.tenantId) autoImport.tenantId = tenantId;
    if (!autoImport.tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        const response = await importHttp.post(
            `/v2/import/tenant/${tenantId}/auto-import`,
            autoImport,
        );
        return response.data;
    } catch (error) {
        console.error('Error creating auto import', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

/**
 * Updates an existing auto import
 *
 * @param {AutoImport} autoImport - The updated auto import
 * @returns {Promise<object>} A promise that resolves to the updated auto import
 */
export const updateAutoImport = async autoImport => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        const response = await importHttp.put(
            `/v2/import/tenant/${tenantId}/auto-import`,
            autoImport,
        );
        return response.data;
    } catch (error) {
        console.error('Error updating auto import', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

/**
 * Deletes an auto import
 *
 * @param {number} id - The ID of the auto import to delete.
 * @returns {Promise<void>}
 */
export const deleteAutoImport = async id => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        await importHttp.delete(
            `/v2/import/tenant/${tenantId}/auto-import/${id}`,
        );
    } catch (error) {
        console.error('Error deleting auto import', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

export const getAutoImportFolders = async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
        console.error('Tenant ID missing.');
        return null;
    }

    try {
        const response = await importHttp.get(
            `/v2/import/tenant/${tenantId}/auto-import-folder`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import data:', error);
        return null;
    }
};

/**
 * Deletes an import
 *
 * @param {number} id - The ID of the import to delete.
 * @returns {Promise<void>}
 */
export const deleteImport = async id => {
    const tenantId = getTenantId();
    if (!tenantId) {
        throw new Error('Tenant ID missing.');
    }

    try {
        var response = await importHttp.delete(
            `/v2/import/tenant/${tenantId}/import/${id}`,
        );
        return response;
    } catch (error) {
        console.error('Error deleting import', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

export const fetchMultiTenantImports = async (
    startDate,
    endDate,
    search,
    onlyFavorites,
    sort,
    order,
    page,
    pageSize,
) => {
    try {
        const queryString = qs.stringify(
            {
                startDate,
                endDate,
                search,
                onlyFavorites,
                sort,
                order,
                page,
                pageSize,
            },
            { arrayFormat: 'repeat' },
        );

        const response = await importHttp.get(
            `/v2/import/tenant/0/import/multi-tenant-search?${queryString}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error retrieving import data:', error);
        return null;
    }
};

export const deleteImportReview = async importId => {
    try {
        var response = await importHttp.delete(
            `/v2/import/import-review/${importId}`,
        );
        return response;
    } catch (error) {
        console.error('Error deleting import review', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

export const addImportReview = async importId => {
    try {
        var response = await importHttp.post(
            `/v2/import/import-review`,
            importId,
        );
        return response;
    } catch (error) {
        console.error('Error adding import review', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

export const testMap = async payload => {
    try {
        const tenantId = getTenantId();
        if (!tenantId) {
            throw new Error('Tenant ID missing.');
        }

        var response = await importHttp.post(
            `/v2/import/tenant/${tenantId}/import-template/map-test`,
            payload,
        );

        return response;
    } catch (error) {
        console.error('Error testing map', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

/**
 * @typedef {Object} CustomImportTemplatePayload
 * @property {number} id - The ID of the import template.
 * @property {number} tenantId - The ID of the tenant associated with the import template.
 * @property {string} name - The name of the import template.
 * @property {null|string} customDelimiter - The custom delimiter used in the import template.
 * @property {boolean} hasHeaderRecord - Indicates if the import template has a header record.
 * @property {null|number} importDelimiterId - The ID of the import delimiter.
 * @property {number} importFileFormatId - The ID of the import file format.
 * @property {boolean} hasTextQualifier - Indicates if the import template has a text qualifier.
 * @property {string|null} sheetName - The name of the sheet in the import template.
 * @property {number|null} importTypeId - Legacy top-level import type used for backend compatibility. For multi templates, FE should send the first template type's importTypeId.
 * @property {TemplateTypePayload[]} templateTypes - The import types defined under this template.
 */

/**
 * @typedef {Object} TemplateTypePayload
 * @property {number} id - The ID of the template type.
 * @property {number} importTemplateId - The parent import template ID.
 * @property {number|null} importTypeId - The import type ID for this template type.
 * @property {string} name - The display name for this template type.
 * @property {number} processingOrder - The display/processing order of this template type.
 * @property {TemplateFieldPayload[]} templateFields - The fields defined for this template type.
 * @property {ImportMapGroupPayload[]} importMapGroups - The mappings defined for this template type.
 */

/**
 * @typedef {Object} TemplateFieldPayload
 * @property {number} id - The ID of the template field.
 * @property {number} importTemplateId - The parent import template ID.
 * @property {number} [importTemplateTypeId] - The parent template type ID.
 * @property {number|null} importFieldId - The destination import field ID.
 * @property {string} [importFieldName] - The destination import field name.
 * @property {number} [columnNumber] - Column number for delimited/excel formats.
 * @property {string} [headerName] - Source header name when relevant.
 * @property {number} [startingLocation] - Start position for fixed-length formats.
 * @property {number} [length] - Field length for fixed-length formats.
 */

/**
 * @typedef {Object} ImportMapGroupPayload
 * @property {number} id - The ID of the map group.
 * @property {number} [importTemplateId] - The parent import template ID.
 * @property {number} [importTemplateTypeId] - The parent template type ID.
 * @property {number} importMapGroupTypeId - The map group type ID.
 * @property {number} processingOrder - The processing order of the map group.
 * @property {number|null} importFieldId - The destination import field ID.
 * @property {string} [importFieldName] - The destination import field name.
 * @property {string} [errorMessage] - Error message emitted by the map.
 * @property {string} [warningMessage] - Warning message emitted by the map.
 * @property {TranslationMapPayload[]} translationMaps - Translation map rows for translation-type groups.
 * @property {FormulaMapPayload|null} formulaMap - Formula map for formula-type groups.
 */

/**
 * @typedef {Object} TranslationMapPayload
 * @property {number} id - The ID of the translation map row.
 * @property {number} processingOrder - The order of the translation map row.
 * @property {string|null} from - The source value to translate from.
 * @property {string|null} to - The destination value to translate to.
 * @property {boolean} else - Indicates whether this is the fallback else row.
 */

/**
 * @typedef {Object} FormulaMapPayload
 * @property {number} id - The ID of the formula map.
 * @property {string} value - The value produced by the formula map when matched.
 * @property {FormulaMapEvaluationPayload[]} evaluations - The evaluations that determine whether the formula map matches.
 */

/**
 * @typedef {Object} FormulaMapEvaluationPayload
 * @property {number} id - The ID of the formula map evaluation.
 * @property {string} leftExpression - The left-hand expression.
 * @property {string} rightExpression - The right-hand expression.
 * @property {number|null} operatorId - The selected operator ID.
 */

/**
 * @typedef {Object} ImportTemplate
 * @property {number} id - The ID of the import template.
 * @property {string} name - The name of the import template.
 * @property {string} type - The display type of the import template. Multi-type templates may return "Multi Import".
 * @property {number} importTypeId - The import type ID for the list row. Multi-type templates may return 0.
 * @property {number} tenantId - The ID of the tenant associated with the import template.
 * @property {string} delimiter - The delimiter used in the import template (only for delimited files).
 * @property {string} fileFormat - The file format of the import template.
 * @property {string|null} importTemplateType - Specific template type name for custom-template queue rows (e.g. "Vision")
 * @property {number|null} importTemplateTypeId - Template type identifier for the row
 * @property {number|null} batchId - Shared batch identifier used to group related queue rows for deletion
 */

/**
 * @typedef {Object} ImportFileFormat
 * @property {number} id - The ID of the import file format.
 * @property {string} name - The name of the import file format.
 */

/**
 * @typedef {Object} ImportFileType
 * @property {number} id - The ID of the import file type.
 * @property {string} name - The name of the import file type.
 */

/**
 * @typedef {Object} ImportType
 * @property {number} id - The ID of the import type.
 * @property {string} name - The name of the import type.
 */

/**
 * @typedef {Object} ImportDelimiter
 * @property {number} id - The ID of the import delimiter.
 * @property {string} name - The name of the import delimiter.
 */

/**
 * @typedef {Object} ImportField
 * @property {number} id - The ID of the import field.
 * @property {string} name - The name of the import field.
 */

/**
 * @typedef {Object} AutoImport
 * @property {number} id - The ID of the auto import.
 * @property {number} tenantId - The tenant ID of the auto import.
 * @property {string} name - The name of the auto import.
 * @property {number} importTypeId - The import type
 * @property {string} path - the path after the root folder on the sftp server
 * @property {boolean} isActive - the enable/disable flag
 * @property {number} importTemplateId - the template the auto import should use.  Null if standard template
 * @property {boolean} isTest - the test flag
 * @property {number} errorThresholdPercentage - number 0 - 100
 * @property {number} importFileTypeId - the file type
 */
